import { ChevronRightIcon } from '@langchain/untitled-ui-icons';
import { Button } from '@mui/joy';

import { MutatorCallback, MutatorOptions } from 'swr';

import { IFrameTraceRenderer } from '@/components/IFrameTraceRenderer';
import { Skeleton } from '@/components/Skeleton';
import { useIFrameTraceRendering } from '@/hooks/useIFrameTraceRendering';
import { getS3StableKey, useS3RunInputOutputs } from '@/hooks/useSwr';
import { DiffChangeType } from '@/types/diff';
import {
  DatasetDataType,
  ExampleSchemaWithRunsAndOptionalFields,
  RunOutputSchema,
  RunSchema,
} from '@/types/schema';

import { ExperimentLanguage, TExamplePages } from '../../types';
import { DatasetSessionCompareSidePaneDetailOutputs } from '../DatasetSessionCompareSidePaneDetailOutputs';

interface OutputRendererProps {
  detailExample: ExampleSchemaWithRunsAndOptionalFields;
  columnId: string;
  run: RunSchema;
  dragging: boolean;
  language?: ExperimentLanguage;
  dataType: DatasetDataType;
  onOpenRepetitionsSidebar: (columnId: string) => void;
  diffs?: DiffChangeType[];
  mutateInfiniteExamples: (
    updateFn?: MutatorCallback<TExamplePages>,
    options?: MutatorOptions
  ) => void;
}

export function CompareOutputContent({
  detailExample,
  columnId,
  run,
  dragging,
  language,
  dataType,
  onOpenRepetitionsSidebar,
  diffs,
  mutateInfiniteExamples,
}: OutputRendererProps) {
  const { currIFrameConfig } = useIFrameTraceRendering(
    detailExample.dataset_id
  );
  const runs = detailExample.runs.filter((r) => r.session_id === columnId);
  const showRepetitionsUI = runs.length > 1;

  const { data: s3Outputs, isLoading: outputsLoading } =
    useS3RunInputOutputs<'outputs'>(
      run.outputs_s3_urls?.['ROOT']?.presigned_url
        ? getS3StableKey(
            `${run.id}-outputs`,
            run.outputs_s3_urls['ROOT'].presigned_url
          )
        : null,
      {
        // update the example runs to inject the outputs
        onSuccess: (data: RunOutputSchema) => {
          mutateInfiniteExamples(
            (currentData) => {
              if (!currentData) return currentData;
              const newData = currentData.map((page) =>
                page.map((example) => {
                  if (example.id === detailExample.id) {
                    return {
                      ...example,
                      runs: example.runs.map((r) =>
                        r.id === run.id ? { ...r, outputs: data } : r
                      ),
                    };
                  }
                  return example;
                })
              );
              return newData;
            },
            { revalidate: false }
          );
        },
      }
    );

  const outputs = s3Outputs ?? run.outputs;

  if (outputsLoading) {
    return (
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (showRepetitionsUI) {
    return (
      <div className="flex">
        <Button
          variant="plain"
          color="neutral"
          size="sm"
          endDecorator={<ChevronRightIcon className="h-4 w-4" />}
          onClick={() => onOpenRepetitionsSidebar(columnId)}
        >
          <div className="font-bold uppercase">{runs.length} reps</div>
        </Button>
      </div>
    );
  }

  if (currIFrameConfig?.enabled && outputs && language === 'pretty') {
    return (
      <IFrameTraceRenderer
        config={currIFrameConfig}
        payload={outputs}
        key={`${detailExample.id}-${columnId}`}
        style={{ pointerEvents: dragging ? 'none' : 'auto' }}
        height="100%"
        type="output"
      />
    );
  }

  return (
    <DatasetSessionCompareSidePaneDetailOutputs
      inputs={detailExample?.inputs}
      outputs={outputs}
      error={run?.error}
      dataType={dataType}
      language={language}
      diffs={diffs}
    />
  );
}
